import theme from '../../../theme';
import { SxProps, Theme, keyframes } from '@mui/material/styles';

type Quality = 'idle' | 'testing' | 'excellent' | 'good' | 'fair' | 'poor' | 'error';

const sizePx = (size: 'small' | 'medium' | 'large') => (size === 'small' ? 24 : size === 'large' ? 40 : 32);

const iconSizePx = (size: 'small' | 'medium' | 'large') => (size === 'small' ? 20 : size === 'large' ? 34 : 26);

export const colorFor = (q: Quality) => {
  const mutedBlue = '#7AA3C4';
  const dustyTeal = '#6b9e90';
  const warmSand = '#b7ad6c';
  const dustyRose = '#9f626c';
  const slate = '#94a3b8';

  switch (q) {
    case 'excellent':
      return mutedBlue;
    case 'good':
      return dustyTeal;
    case 'fair':
      return warmSand;
    case 'poor':
      return dustyRose;
    case 'idle':
    case 'error':
    default:
      return slate;
  }
};

export const iconFor = (q: Quality): string => {
  switch (q) {
    case 'excellent':
    case 'good':
      return '/assets/internetQuality/good.svg';
    case 'fair':
      return '/assets/internetQuality/average.svg';
    case 'poor':
      return '/assets/internetQuality/bad.svg';
    case 'idle':
    case 'error':
    default:
      return '/assets/internetQuality/noConnection.svg';
  }
};

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(0,0,0,0.0); }
  70%  { box-shadow: 0 0 0 6px rgba(0,0,0,0.04); }
  100% { box-shadow: 0 0 0 0 rgba(0,0,0,0.0); }
`;

export const nqiStyles = {
  button: (_q: Quality, size: 'small' | 'medium' | 'large' = 'medium'): SxProps<Theme> => ({
    width: sizePx(size),
    height: sizePx(size),
    p: 0,
    borderRadius: theme.palette?.propVars?.borderRadius ?? '50%',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    marginRight: '0.2em',
    '&:hover': { backgroundColor: 'transparent' },
    '&.Mui-disabled': { opacity: 0.5 },
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),

  icon: (color: string, iconUrl: string, size: 'small' | 'medium' | 'large' = 'medium'): SxProps<Theme> => {
    const d = iconSizePx(size);
    const maskUrl = `url("${iconUrl}")`;

    return {
      width: d,
      height: d,
      backgroundColor: color,
      WebkitMaskImage: maskUrl,
      maskImage: maskUrl,
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain',
      transform: 'translateY(1px)',
    };
  },

  spinner: {
    color: theme.palette?.myPrimary?.lightBlue100 ?? theme.palette.primary.main,
  } as SxProps<Theme>,

  alertPulse: {
    animation: `${pulse} 1.8s ease-out infinite`,
  } as SxProps<Theme>,
};
